import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

export type StatoOperazione = "in-corso" | "completata" | "errore";

export interface AzioneCompletamento {
  label: string;
  icon?: string;
  azione: (risultato: any, rimuovi: () => void) => void;
}

export interface BackgroundOperation {
  id: string;
  titolo: string;
  messaggio: string;
  stato: StatoOperazione;
  progresso: number; // 0..100, -1 = indeterminato
  dataInizio: Date;
  dataFine: Date | null;
  risultato: any;
  azione: AzioneCompletamento | null;
  errore: string | null;
}

@Injectable({ providedIn: "root" })
export class BackgroundOperationService {
  private operazioniSubject = new BehaviorSubject<BackgroundOperation[]>([]);
  public operazioni$: Observable<BackgroundOperation[]> = this.operazioniSubject.asObservable();

  private counter: number = 0;

  private nextId(): string {
    this.counter += 1;
    return "op-" + Date.now() + "-" + this.counter;
  }

  avvia(titolo: string, messaggio: string): BackgroundOperation {
    const op: BackgroundOperation = {
      id: this.nextId(),
      titolo: titolo,
      messaggio: messaggio,
      stato: "in-corso",
      progresso: -1,
      dataInizio: new Date(),
      dataFine: null,
      risultato: null,
      azione: null,
      errore: null,
    };
    const lista = this.operazioniSubject.getValue().slice();
    lista.push(op);
    this.operazioniSubject.next(lista);
    return op;
  }

  /**
   * Esegue una Promise tracciandola come operazione in background.
   * Finché la Promise è pendente l'operazione è "in-corso"; al resolve viene
   * completata con il valore risolto come risultato, e l'azione (se fornita)
   * la riceve come parametro al click del pulsante.
   */
  esegui<T>(
    titolo: string,
    messaggio: string,
    operazione: () => Promise<T>,
    messaggioCompletamento?: string,
    azione?: AzioneCompletamento
  ): BackgroundOperation {
    const op = this.avvia(titolo, messaggio);
    Promise.resolve()
      .then(() => operazione())
      .then(risultato => {
        this.completa(op.id, risultato, messaggioCompletamento, azione);
      })
      .catch(err => {
        const msg = err && err.message ? err.message : String(err);
        this.errore(op.id, msg);
      });
    return op;
  }

  aggiornaProgresso(id: string, progresso: number, messaggio?: string): void {
    const lista = this.operazioniSubject.getValue().slice();
    const idx = lista.findIndex(o => o.id === id);
    if (idx === -1) {
      return;
    }
    lista[idx] = Object.assign({}, lista[idx], {
      progresso: progresso,
      messaggio: messaggio != null ? messaggio : lista[idx].messaggio,
    });
    this.operazioniSubject.next(lista);
  }

  completa(
    id: string,
    risultato?: any,
    messaggio?: string,
    azione?: AzioneCompletamento
  ): void {
    const lista = this.operazioniSubject.getValue().slice();
    const idx = lista.findIndex(o => o.id === id);
    if (idx === -1) {
      return;
    }
    lista[idx] = Object.assign({}, lista[idx], {
      stato: "completata" as StatoOperazione,
      progresso: 100,
      dataFine: new Date(),
      risultato: risultato != null ? risultato : null,
      azione: azione != null ? azione : null,
      messaggio: messaggio != null ? messaggio : "Completata",
    });
    this.operazioniSubject.next(lista);
  }

  errore(id: string, errore: string): void {
    const lista = this.operazioniSubject.getValue().slice();
    const idx = lista.findIndex(o => o.id === id);
    if (idx === -1) {
      return;
    }
    lista[idx] = Object.assign({}, lista[idx], {
      stato: "errore" as StatoOperazione,
      dataFine: new Date(),
      errore: errore,
      messaggio: "Errore: " + errore,
    });
    this.operazioniSubject.next(lista);
  }

  rimuovi(id: string): void {
    const lista = this.operazioniSubject.getValue().filter(o => o.id !== id);
    this.operazioniSubject.next(lista);
  }

  svuotaCompletate(): void {
    const lista = this.operazioniSubject.getValue().filter(o => o.stato === "in-corso");
    this.operazioniSubject.next(lista);
  }
}
